import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import DownloadIcon from '@mui/icons-material/Download';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listenToTenderSections } from '../../services/tenderSectionService';
import type { Tender } from '../../types/tender';
import { calculateTenderStep } from '../../utils/tenderProgress';

interface TenderActionStepperProps {
  tender: Tender;
  onDownload: (type: 'alignment' | 'response' | 'review' | 'compile') => void;
  isProcessing: (type: 'analysis' | 'response' | 'review' | 'compile') => boolean;
  isLoading: (type: string) => boolean;
}

const steps = [
  {
    step: 2,
    label: 'Analysis',
    docType: 'alignment' as const,
    processingType: 'analysis' as const,
    optional: false,
  },
  {
    step: 6,
    label: 'Draft',
    docType: 'response' as const,
    processingType: 'response' as const,
    optional: false,
  },
  {
    step: 7,
    label: 'Section Review',
    optional: false,
  },
  {
    step: 8,
    label: 'Create DOCX',
    docType: 'compile' as const,
    processingType: 'compile' as const,
    optional: false,
  },
  {
    step: 11,
    label: 'Final Review',
    docType: 'review' as const,
    processingType: 'review' as const,
    optional: false,
  },
];

const TenderActionStepper = ({
  tender,
  onDownload,
  isProcessing,
  isLoading,
}: TenderActionStepperProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { step: currentStep } = calculateTenderStep(tender);

  const [reviewPercent, setReviewPercent] = useState<number | null>(null);

  useEffect(() => {
    // Only listen for sections if we are at the revise stage or beyond, and tender ID exists
    if (!tender.id || currentStep < 6) {
      return;
    }

    const unsubscribe = listenToTenderSections(
      tender.id,
      (sections) => {
        let totalWords = 0;
        let reviewedWords = 0;
        let reviewedSections = 0;

        for (const data of sections) {
          // Fallback to estimating word count if it's missing but there's encrypted content
          const wc = data.wordCount ?? Math.round((data.encrypted_content?.length || 0) / 8);
          totalWords += wc;
          if (data.status === 'reviewed' || data.status === 'complete') {
            reviewedWords += wc;
            reviewedSections++;
          }
        }

        if (totalWords > 0) {
          setReviewPercent(Math.round((reviewedWords / totalWords) * 100));
        } else if (sections.length > 0) {
          setReviewPercent(Math.round((reviewedSections / sections.length) * 100));
        } else {
          setReviewPercent(0);
        }
      },
      (error) => {
        // biome-ignore lint/suspicious/noConsole: Error logging
        console.error('Firebase snapshot error on TenderActionStepper sections:', error);
      }
    );

    return () => unsubscribe();
  }, [tender.id, currentStep]);

  const handleStepClick = (step: number) => {
    let targetStep = 1;
    if (step === 2) {
      targetStep = 2; // Analysis
    }
    if (step === 6) {
      targetStep = 6; // Draft
    }
    if (step === 7) {
      targetStep = 7; // Review (Step 7 in Details)
    }
    if (step === 8) {
      targetStep = 8; // Create DOCX (Step 8 in Details)
    }
    if (step === 11) {
      targetStep = 11; // Final Review (Step 11 in Details)
    }
    navigate(`/tender/${tender.id}?step=${targetStep}`);
  };

  const hasDoc = (docType?: 'alignment' | 'response' | 'review' | 'compile') => {
    if (docType === 'alignment') {
      return !!tender.analysisFileLink;
    }
    if (docType === 'response') {
      const draftStatus = tender.draftResponse?.status?.toLowerCase();
      return (
        !!tender.draftFileLink ||
        draftStatus === 'sections generated' ||
        draftStatus === 'complete' ||
        draftStatus === 'success' ||
        draftStatus === 'draft complete'
      );
    }
    if (docType === 'review') {
      return !!tender.reviewFileLink;
    }
    if (docType === 'compile') {
      return !!tender.compiledFileLink;
    }
    return false;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        justifyContent: 'space-between',
        position: 'relative',
        px: 1,
      }}
    >
      {/* Connector Line */}
      <Box
        sx={{
          position: 'absolute',
          top: '18px', // Center of the 36px icon button (approx)
          left: '10%',
          right: '10%',
          height: '2px',
          bgcolor: theme.palette.greyContrast,
          zIndex: 0,
        }}
      />

      {steps.map((s, _index) => {
        // Logic check: step is completed if currentStep > s.step.
        const isPassed = currentStep > s.step;
        const isCurrent = currentStep === s.step;
        const processing = s.processingType ? isProcessing(s.processingType) : false;

        let StepIcon = RadioButtonUncheckedIcon;
        let color = theme.palette.grey[400];
        let tooltip = `${s.label}`;
        let borderColor = isCurrent ? theme.palette.bidlogiq.brick : theme.palette.background.paper;

        const isFormOnly = tender.quickSettings?.draftFormatMode === 'form_only';

        if (isPassed) {
          StepIcon = CheckCircleIcon;
          // Explicitly use green for completed steps
          color = '#2e7d32';
          tooltip = `${s.label} (Completed)`;
        } else if (isCurrent) {
          StepIcon = CircleIcon;
          color = theme.palette.bidlogiq.brick;
          tooltip = `${s.label} (Current)`;
        } else {
          // Future Step
          if (s.optional) {
            StepIcon = CheckCircleIcon; // Grey tick for optional future
            color = theme.palette.grey[400];
            tooltip = `${s.label} (Optional)`;
          } else {
            // Mandatory Future -> Open Circle
            StepIcon = RadioButtonUncheckedIcon;
            color = theme.palette.grey[400];
            tooltip = `${s.label}`;
          }
        }

        // Apply form_only override for inapplicable steps
        if (isFormOnly && (s.step === 7 || s.step === 8 || s.step === 11)) {
          StepIcon = RadioButtonCheckedIcon;
          color = isCurrent ? theme.palette.bidlogiq.brick : theme.palette.grey[400];
          tooltip = `${s.label} (N/A for Forms)`;
          if (isCurrent) {
            borderColor = color;
          }
        }

        // Override for Draft (Step 6) if sections are generated
        if (s.step === 6 && reviewPercent !== null) {
          StepIcon = CheckCircleIcon;
          color = '#2e7d32'; // strong green
          if (isCurrent) {
            borderColor = color;
          }
        }

        // Override for Section Review (Step 7) based on review percent
        if (s.step === 7 && reviewPercent !== null) {
          if (reviewPercent === 100) {
            StepIcon = CheckCircleIcon;
            color = '#2e7d32'; // strong green
          } else {
            color =
              reviewPercent >= 70
                ? '#81c784' // light green
                : reviewPercent >= 30
                  ? '#ffb74d' // orange/yellow
                  : '#e57373'; // red
          }
          if (isCurrent) {
            borderColor = color;
          }
        }

        const docAvailable = s.docType && hasDoc(s.docType);

        return (
          <Box
            key={s.step}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 1,
              position: 'relative',
              width: '20%',
            }}
          >
            <Tooltip title={processing ? `${s.label} (Processing...)` : tooltip}>
              <IconButton
                onClick={() => handleStepClick(s.step)}
                sx={{
                  p: 0.5,
                  bgcolor: theme.palette.background.paper,
                  border: `2px solid ${borderColor}`,
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                  width: 36,
                  height: 36,
                }}
              >
                {processing ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.bidlogiq.brick }} />
                ) : (
                  <StepIcon sx={{ color, fontSize: '24px' }} />
                )}
              </IconButton>
            </Tooltip>

            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: isCurrent ? theme.palette.text.primary : theme.palette.text.secondary,
                fontWeight: isCurrent ? 700 : 400,
                mt: 0.5,
                textAlign: 'center',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {s.label}
            </Typography>

            {/* Download Button or Progress */}
            <Box
              sx={{
                mt: 0.5,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {s.docType && docAvailable ? (
                <Tooltip title={`Download ${s.label}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (s.docType) {
                        onDownload(s.docType);
                      }
                    }}
                    disabled={
                      isLoading(s.docType) ||
                      (s.processingType ? isProcessing(s.processingType) : false)
                    }
                    sx={{
                      p: 0.5,
                      color: theme.palette.bidlogiq.brick,
                      bgcolor: theme.palette.action.hover,
                      '&:hover': { bgcolor: theme.palette.action.selected },
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
              ) : s.step === 7 && reviewPercent !== null ? (
                <Chip
                  label={<strong>{reviewPercent}%</strong>}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.8rem',
                    bgcolor:
                      reviewPercent === 100
                        ? '#2e7d32' // strong green
                        : reviewPercent >= 70
                          ? '#81c784' // light green
                          : reviewPercent >= 30
                            ? '#ffb74d' // orange/yellow
                            : '#e57373', // red
                    color: reviewPercent === 100 ? 'white' : 'black',
                  }}
                />
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default TenderActionStepper;
